import { isTerminalStatus, UploadItemStatus } from './upload-status'

/** The subset of an upload item this derivation needs — keeps it testable without a store. */
interface SummarizedItem {
	status: UploadItemStatus
}

/** Cross-app upload progress, for the status-bar chip. */
export interface UploadSummary {
	/**
	 * Items in the CURRENT run — outstanding ones plus those that have settled
	 * since the last time nothing was in flight. Not a lifetime count: the chip
	 * reads "Uploading {settled}/{total}", and a session total makes that say
	 * "4/6" while a single file is actually moving.
	 */
	total: number
	/** Items in that run that finished successfully. */
	settled: number
	/** Items still doing something. Zero means the chip should disappear. */
	outstanding: number
}

/**
 * {@link UploadSummary} for a given set of items.
 *
 * `total` counts only the items whose work belongs to the run still in
 * progress. Once nothing is outstanding the chip is hidden anyway, so the
 * distinction is only ever visible while something is moving — which is exactly
 * when a stale denominator misleads.
 */
export function summarize(list: SummarizedItem[]): UploadSummary {
	const outstanding = list.filter((item) => !isTerminalStatus(item.status))
	// Nothing moving: the chip is hidden, so report the whole list rather than
	// inventing a zero the callers would have to special-case.
	if (outstanding.length === 0) {
		return {
			total: list.length,
			settled: list.filter((item) => item.status === UploadItemStatus.SETTLED).length,
			outstanding: 0,
		}
	}

	// A run is delimited by the oldest item still working: everything enqueued
	// from that point on is part of the same burst, and anything that settled
	// before it belongs to an earlier one the user has already seen finish.
	const firstOutstandingIndex = list.findIndex((item) => !isTerminalStatus(item.status))
	const run = list.slice(firstOutstandingIndex)
	return {
		total: run.length,
		// An item that finished with a failure is terminal — it counts toward
		// `total` and never toward `settled`, because it is finished, just not
		// successfully, and nothing more happens without a retry.
		settled: run.filter((item) => item.status === UploadItemStatus.SETTLED).length,
		outstanding: outstanding.length,
	}
}
